import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useSiteService } from "~pages/Sites/hooks/useSiteService.ts";
import { defaultSitesExportFieldNames, sitesExportFields } from "~pages/Sites/SiteListPage/constants/exportFields.ts";
import { useFetchSites } from "~pages/Sites/SiteListPage/hooks/useFetchSites.ts";
import { useGetSiteTableColumns } from "~pages/Sites/SiteListPage/hooks/useGetSiteTableColumns.tsx";
import { useGetSiteTableFilters } from "~pages/Sites/SiteListPage/hooks/useGetSiteTableFilters.tsx";

const SiteListPage = () => {
  const paginationConfig = useHandlePagination(20, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/sites/manage/`);
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [sortBy, setSortBy] = useState<string | null>(null);

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.sites.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.sites.listPage.importConfig;
  const filterConfig = useGetSiteTableFilters();

  const { data, refetch, isPending } = useFetchSites({
    enabled: sortBy !== null,
    customParams: {
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      countryCode: filterConfig.currentFilter.countryCode?.value,
      nameLocalContains: searchText,
    },
  });

  const { deleteSite, isDeletePending, importSites, exportSites, isExportPending } = useSiteService();

  const handleDelete = useCallback(
    async (siteId: string) => {
      return await deleteSite(siteId);
    },
    [deleteSite],
  );

  const columns = useGetSiteTableColumns();

  const dataSource = data?.data || [];

  const getNavigateUrl = (siteId: string) => `/${customerId}/admin/${sport}/${organizationId}/sites/manage/${siteId}`;

  return (
    <DataTable
      handleRefresh={async () => {
        await refetch();
      }}
      title={formatMessage({ id: "siteListPage.title" })}
      columns={columns}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => handleDelete(row.siteId),
            loading: isDeletePending,
          },
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.siteId)),
          },
        },
      }}
      rowKey={"siteId"}
      rowNameKey={"nameLocal"}
      dataSource={dataSource}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.sites`}
      isDataLoading={isPending}
      handleAdd={handleAdd}
      addButtonIntlKey={"site"}
      filtersConfig={filterConfig}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.sites`,
        defaultSorter: {
          columnKey: "nameLocal",
          order: "descend",
        },
        onSortChange: setSortBy,
      }}
      exportConfig={{
        handleExport: exportSites,
        loading: isExportPending,
        defaultFields: defaultSitesExportFieldNames,
        groupedFields: sitesExportFields,
        localStorageKey: "export.sites",
      }}
      importConfig={{
        ...dataTableImportConfiguration,
        handleImport: importSites,
      }}
      permissionNames={[PermissionNames.SITE]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default SiteListPage;
